import { useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { getAllOrders } from '../../services/orders';
import { ENDPOINTS } from '../../utils/constants';
import './PrintQRSheet.css';

/**
 * Standalone print page — compact grid of QR codes for filtered orders.
 * Opened in a new tab by the employee Orders page.
 *
 * URL params (all optional, match employee order list filters):
 *   search, status, dateFrom, dateTo, session
 */
const PrintQRSheet = () => {
  const [params] = useState(() => new URLSearchParams(window.location.search));
  const filters = {
    search:   params.get('search')   || '',
    status:   params.get('status')   || '',
    dateFrom: params.get('dateFrom') || '',
    dateTo:   params.get('dateTo')   || '',
    session:  params.get('session')  || '',
  };

  const [orders, setOrders]     = useState([]);
  const [qrBlobs, setQrBlobs]   = useState({});
  const [qrsDone, setQrsDone]   = useState(false);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const blobUrlsRef = useRef([]);

  // 1. Fetch filtered orders
  useEffect(() => {
    getAllOrders(filters)
      .then((data) => {
        setOrders(data.results ?? data);
        setLoading(false);
      })
      .catch(() => {
        setError('Could not load orders. Make sure you are logged in.');
        setLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 2. Fetch QR images via authenticated API and create blob URLs
  useEffect(() => {
    if (orders.length === 0) return;

    let cancelled = false;

    const loadQRs = async () => {
      const blobs = {};
      await Promise.all(
        orders.map(async (order) => {
          try {
            const res = await api.get(
              ENDPOINTS.ORDERS.EMPLOYEE.QR_CODE(order.id),
              { responseType: 'blob' },
            );
            if (!cancelled) {
              const url = URL.createObjectURL(res.data);
              blobs[order.id] = url;
              blobUrlsRef.current.push(url);
            }
          } catch {
            // skip — cell will show placeholder
          }
        }),
      );
      if (!cancelled) {
        setQrBlobs(blobs);
        setQrsDone(true);
      }
    };

    loadQRs();
    return () => { cancelled = true; };
  }, [orders]);

  // 3. Auto-print once all QR fetches have settled
  useEffect(() => {
    if (!loading && orders.length > 0 && qrsDone) {
      const timer = setTimeout(() => window.print(), 600);
      return () => clearTimeout(timer);
    }
  }, [loading, orders, qrsDone]);

  // Revoke blob URLs on unmount
  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach(URL.revokeObjectURL);
    };
  }, []);

  if (error) {
    return <div className="print-qr-page print-qr--error"><p>{error}</p></div>;
  }

  if (loading) {
    return <div className="print-qr-page print-qr--loading"><p>Loading orders…</p></div>;
  }

  if (orders.length === 0) {
    return <div className="print-qr-page print-qr--empty"><p>No orders match the current filters.</p></div>;
  }

  return (
    <div className="print-qr-page">
      {/* Screen-only toolbar */}
      <div className="print-qr__toolbar no-print">
        <span className="print-qr__toolbar-label">
          QR Sheet — {orders.length} {orders.length === 1 ? 'order' : 'orders'}
        </span>
        <button
          type="button"
          className="print-qr__print-btn"
          onClick={() => window.print()}
        >
          Print
        </button>
      </div>

      {/* Printable grid */}
      <div className="print-qr__grid">
        {orders.map((order) => (
          <div key={order.id} className="print-qr__cell">
            {qrBlobs[order.id] ? (
              <img
                src={qrBlobs[order.id]}
                alt={`QR for ${order.order_number}`}
                className="print-qr__img"
              />
            ) : (
              <div className="print-qr__img print-qr__img--placeholder" />
            )}
            <p className="print-qr__order-number">{order.order_number}</p>
            <p className="print-qr__customer">
              {order.company_name || order.company_detail?.company_name || '—'}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PrintQRSheet;
